<template>
  <v-menu
    v-if="showSwitcher"
    location="bottom start"
    offset="4"
    :close-on-content-click="false"
    @update:model-value="onMenuToggle"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        icon
        size="x-small"
        variant="text"
        :loading="switching"
        aria-label="Switch company"
      >
        <v-icon size="18">mdi-chevron-down</v-icon>
        <v-tooltip activator="parent" location="bottom">Switch company</v-tooltip>
      </v-btn>
    </template>

    <v-list density="compact" min-width="280" max-width="360">
      <!-- One unified, searchable list of every company the user can reach.
           Member companies switch in place (no re-login); non-member companies
           (platform admins only) open an audited God Mode session AS the admin
           themselves. A single search box covers the whole list. -->
      <v-list-subheader>{{ isPlatformAdmin ? 'Companies' : 'Switch company' }}</v-list-subheader>
      <div v-if="isPlatformAdmin" class="px-3 pb-2">
        <v-text-field
          v-model="search"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          placeholder="Search all companies..."
          prepend-inner-icon="mdi-magnify"
          :loading="loadingAll"
        />
      </div>
      <div class="company-switcher-scroll">
        <v-list-item
          v-for="c in unifiedList"
          :key="`c-${c.id}`"
          :active="c.code === currentCode"
          :disabled="switching"
          @click="onPick(c)"
        >
          <template #prepend>
            <v-icon size="18" :color="(c.is_member || c.code === currentCode) ? undefined : 'error'">
              {{ c.code === currentCode ? 'mdi-check-circle' : (c.is_member ? 'mdi-domain' : 'mdi-open-in-new') }}
            </v-icon>
          </template>
          <v-list-item-title>{{ c.name }}</v-list-item-title>
          <template #append>
            <v-chip v-if="c.is_platform" size="x-small" variant="tonal" color="primary">HQ</v-chip>
            <v-chip v-else-if="c.is_member" size="x-small" variant="tonal" color="success">Member</v-chip>
            <v-chip v-else size="x-small" variant="tonal" color="error">Admin</v-chip>
          </template>
        </v-list-item>
        <v-list-item v-if="isPlatformAdmin && !loadingAll && unifiedList.length === 0">
          <v-list-item-title class="text-medium-emphasis text-caption">No companies found</v-list-item-title>
        </v-list-item>
      </div>
    </v-list>
  </v-menu>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import apiClient from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useNotify } from '@/composables/useNotify'
import { isNative } from '@/native/platform'
import { clearSharedOperationsReport } from '@/views/reports/operationsReportLoad'

const authStore = useAuthStore()
const router = useRouter()
const { notify } = useNotify()

const memberDomains = ref([])
const allCompanies = ref([])
const loadingAll = ref(false)
const loadedAllOnce = ref(false)
const switching = ref(false)
const search = ref('')

const currentCode = computed(() => authStore.domain?.code)
const isPlatformAdmin = computed(() =>
  ['full', 'readonly'].includes(authStore.user?.platform_access),
)

// Visible when the reachable identity can reach more than one company: either
// they belong to 2+ companies, or they are a global admin (who can enter any
// active company). Hidden only in the native technician app.
// God Mode = faithful mirror: inside an impersonation session this reflects the
// IMPERSONATED user (authStore.user / their /auth/domains), so viewing as a
// multi-company operator shows their switcher, and viewing as a single-company
// user shows nothing — exactly what that user would see.
const showSwitcher = computed(() => {
  if (isNative() || !authStore.isAuthenticated) return false
  return memberDomains.value.length > 1 || isPlatformAdmin.value
})

// Single source for the dropdown. Platform admins see EVERY active company
// (allCompanies already carries an is_member flag); everyone else sees only the
// companies they belong to. Ordered: current company first, then the companies
// the user is a member of, then the rest — all alphabetical within each group.
const unifiedList = computed(() => {
  const base = isPlatformAdmin.value
    ? allCompanies.value
    : memberDomains.value.map((d) => ({ ...d, is_member: true }))
  const q = (search.value || '').trim().toLowerCase()
  const list = q
    ? base.filter(
        (c) => (c.name || '').toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q),
      )
    : base.slice()
  const rank = (c) => {
    if (c.code === currentCode.value) return 0
    if (c.is_member) return 1
    return 2
  }
  return list.sort((a, b) => {
    const r = rank(a) - rank(b)
    if (r !== 0) return r
    return (a.name || '').localeCompare(b.name || '')
  })
})

async function loadMembers() {
  try {
    const { data } = await apiClient.get('/auth/domains/')
    const list = Array.isArray(data) ? data : (data.results || [])
    memberDomains.value = list.filter((d) => d && d.id && d.code)
  } catch (e) {
    console.warn('[CompanySwitcher] loadMembers failed:', e?.message || e)
    memberDomains.value = []
  }
}

async function loadAllCompanies() {
  if (!isPlatformAdmin.value || loadedAllOnce.value) return
  loadingAll.value = true
  try {
    const { data } = await apiClient.get('/platform/companies/switcher/')
    allCompanies.value = (data.results || data || []).filter((c) => c && c.id && c.code)
    loadedAllOnce.value = true
  } catch (e) {
    console.warn('[CompanySwitcher] loadAllCompanies failed:', e?.message || e)
    allCompanies.value = []
  } finally {
    loadingAll.value = false
  }
}

function onMenuToggle(open) {
  if (open) loadAllCompanies()
}

async function onPick(c) {
  if (!c || !c.code || switching.value) return
  if (c.code === currentCode.value) return
  // Inside a God Mode session, NEVER switch in place (select-domain would issue
  // normal, non-God tokens and corrupt the session). Re-enter the chosen company
  // through the audited God Mode flow, keeping the currently-viewed identity when
  // allowed (backend decides by tier / membership).
  if (authStore.isGodMode) {
    await switchInGodMode(c)
    return
  }
  // Non-member company (only reachable by a global admin) -> audited God Mode.
  if (c.is_member === false) {
    await enterGodMode(c)
    return
  }
  await switchInPlace(c.code)
}

async function switchInGodMode(c) {
  switching.value = true
  try {
    const { data } = await apiClient.post('/platform/impersonate/', {
      domain_id: c.id,
      keep_user: true,
    })
    // Same-tab re-bootstrap of the God Mode session for the chosen company.
    window.location.assign(
      `${window.location.origin}/impersonate?token=${data.token}`,
    )
    // Navigating away — leave `switching` on so the button stays disabled.
  } catch (e) {
    console.error('[CompanySwitcher] God Mode switch failed:', e?.response || e)
    notify.error(e?.response?.data?.error || 'Failed to switch company')
    switching.value = false
  }
}

async function switchInPlace(code) {
  switching.value = true
  try {
    const result = await authStore.selectDomain(code)
    if (result?.error) {
      notify.error(result.error)
      return
    }
    notify.success(`Switched to ${result.domain?.name || 'company'}`)
    clearSharedOperationsReport()
    window.dispatchEvent(new CustomEvent('fsj-company-changed'))
    await loadMembers()
    // Land on a neutral page so we never show the previous tenant's data.
    router.push('/').catch(() => {})
  } finally {
    switching.value = false
  }
}

async function enterGodMode(c) {
  switching.value = true
  try {
    const { data } = await apiClient.post('/platform/impersonate/', { domain_id: c.id })
    const url = `${window.location.origin}/impersonate?token=${data.token}`
    window.open(url, '_blank')
    notify.success(`God Mode: opening ${c.name}`)
  } catch (e) {
    console.error('[CompanySwitcher] God Mode open failed:', e?.response || e)
    notify.error(e?.response?.data?.error || 'Failed to open company')
  } finally {
    switching.value = false
  }
}

onMounted(() => {
  // Load in God Mode too, so the switcher can faithfully mirror the impersonated
  // user's reachable companies.
  if (!isNative() && authStore.isAuthenticated) {
    loadMembers()
  }
})
</script>

<style scoped>
.company-switcher-scroll {
  max-height: 420px;
  overflow-y: auto;
}
</style>
